import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import {
  ArrowForward,
  ArrowBack,
  ArrowForwardIos,
  ArrowBackIos,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Styled components for carousel
const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  minHeight: '70vh',
  height: '70vh',
  borderRadius: theme.spacing(2),
  '&:hover .carousel-nav': {
    opacity: 1,
  },
}));

const CarouselSlide = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '70vh',
  minHeight: '70vh',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.primary.contrastText,
  cursor: 'pointer',
  flexShrink: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4))',
    zIndex: 1,
  },
}));

const CarouselContent = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  zIndex: 3,
  opacity: 0,
  transition: 'all 0.3s ease',
  width: 50,
  height: 50,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-50%) scale(1.1)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
  '&:active': {
    transform: 'translateY(-50%) scale(0.95)',
  },
  [theme.breakpoints.down('md')]: {
    opacity: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
  },
}));

const IndicatorContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(3),
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: theme.spacing(1),
  zIndex: 3,
}));

const Indicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: isActive ? 'white' : 'rgba(255, 255, 255, 0.4)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  transform: isActive ? 'scale(1.2)' : 'scale(1)',
  border: isActive ? '2px solid rgba(255, 255, 255, 0.8)' : '2px solid transparent',
  boxShadow: isActive ? '0 0 20px rgba(255, 255, 255, 0.5)' : 'none',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    transform: 'scale(1.1)',
  },
}));

const BannerCarousel = ({ banners = [], loading = false }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const autoPlayRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Filter active banners
  const activeBanners = banners.filter(banner => banner.active);

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && activeBanners.length > 1) {
      autoPlayRef.current = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % activeBanners.length);
      }, 5000); // Change slide every 5 seconds
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, activeBanners.length]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && activeBanners.length > 1) {
      nextSlide();
    }
    if (isRightSwipe && activeBanners.length > 1) {
      prevSlide();
    }
  };

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % activeBanners.length);
    pauseAutoPlay();
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + activeBanners.length) % activeBanners.length);
    pauseAutoPlay();
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    pauseAutoPlay();
  };

  const pauseAutoPlay = () => {
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000); // Resume after 10 seconds
  };

  const handleBannerClick = (banner) => {
    if (banner.linkUrl) {
      if (banner.linkUrl.startsWith('http')) {
        window.open(banner.linkUrl, '_blank');
      } else {
        navigate(banner.linkUrl);
      }
    }
  };

  const handleShopNow = (banner, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    // Build query parameters for filtering
    const params = new URLSearchParams();
    
    // Add product name filter if available
    if (banner?.productName) {
      params.set('search', banner.productName);
    }
    
    // Add category filter if available
    if (banner?.category) {
      params.set('category', banner.category);
    }
    
    // Navigate to products page with filters
    const queryString = params.toString();
    navigate(`/products${queryString ? `?${queryString}` : ''}`);
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '70vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: 'grey.100',
        borderRadius: 2,
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  // No banners state
  if (!activeBanners.length) {
    return (
      <Box
        sx={{
          background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url("https://images.pexels.com/photos/7031589/pexels-photo-7031589.jpeg?_gl=1*1e71lnm*_ga*MTQ4MzEyODY1NS4xNzU3OTQ4ODQ0*_ga_8JE65Q40S6*czE3NTc5NDg4NDQkbzEkZzEkdDE3NTc5NDk2MjgkajU5JGwwJGgw")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: theme.palette.primary.contrastText,
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          borderRadius: 2,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30, x: -20 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              >
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    color: 'white'
                  }}
                >
                  Premium Building Materials
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    fontWeight: 300,
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                    color: 'white'
                  }}
                >
                  Discover the finest marble, granite, and tiles from verified suppliers across India
                </Typography>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5, ease: "easeOut" }}
                >
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate('/products')}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                        },
                      }}
                    >
                      Shop Now
                    </Button>
                  {/* <Button
                      variant="outlined"
                      color="inherit"
                      size="large"
                      onClick={() => navigate('/about')}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        color: 'white',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Learn More
                    </Button> */}
                  </Box>
                </motion.div>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  // Single banner (backward compatibility)
  if (activeBanners.length === 1) {
    const banner = activeBanners[0];
    return (
      <Box
        sx={{
          background: banner.imageUrl 
            ? `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url("${banner.imageUrl}")`
            : `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url("https://images.pexels.com/photos/7031589/pexels-photo-7031589.jpeg?_gl=1*1e71lnm*_ga*MTQ4MzEyODY1NS4xNzU3OTQ4ODQ0*_ga_8JE65Q40S6*czE3NTc5NDg4NDQkbzEkZzEkdDE3NTc5NDk2MjgkajU5JGwwJGgw")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: theme.palette.primary.contrastText,
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          cursor: banner.linkUrl ? 'pointer' : 'default',
          borderRadius: 2,
        }}
        onClick={() => handleBannerClick(banner)}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    color: 'white'
                  }}
                >
                  {banner.title || 'Premium Building Materials'}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    fontWeight: 300,
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                    color: 'white'
                  }}
                >
                  {banner.description || 'Discover the finest marble, granite, and tiles from verified suppliers across India'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={(e) => handleShopNow(banner, e)}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                  >
                    Shop Now
                  </Button>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  // Multiple banners carousel - CodePen style with hover slide effect
  return (
    <CarouselContainer
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      sx={{ overflow: 'hidden', position: 'relative' }}
    >
      {/* Single slide display with hover effect */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          style={{ width: '100%', height: '100%', position: 'relative' }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover .image-layer': {
                transform: 'translateX(-50%)',
              },
              '&:hover .text-layer': {
                opacity: 1,
                transform: 'translateX(0)',
              }
            }}
            onClick={() => handleBannerClick(activeBanners[currentSlide])}
          >
            {/* Image Layer */}
            <Box
              className="image-layer"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: activeBanners[currentSlide]?.imageUrl 
                  ? `url("${activeBanners[currentSlide].imageUrl}")`
                  : `url("https://images.pexels.com/photos/7031589/pexels-photo-7031589.jpeg?_gl=1*1e71lnm*_ga*MTQ4MzEyODY1NS4xNzU3OTQ4ODQ0*_ga_8JE65Q40S6*czE3NTc5NDg4NDQkbzEkZzEkdDE3NTc5NDk2MjgkajU5JGwwJGgw")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                transition: 'transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
                zIndex: 2,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3))',
                  zIndex: 1,
                },
              }}
            />

            {/* Text Layer */}
            <Box
              className="text-layer"
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '50%',
                height: '100%',
                background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.7))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transform: 'translateX(100%)',
                transition: 'all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
                zIndex: 3,
                padding: 4,
              }}
            >
              <Container maxWidth="sm" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`banner-${activeBanners[currentSlide]?.id || currentSlide}-${currentSlide}`}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ 
                      duration: 0.6, 
                      delay: 0.3,
                      ease: "easeOut"
                    }}
                    style={{ width: '100%' }}
                  >
                    <Typography
                      variant="h2"
                      component="h1"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                        color: 'white'
                      }}
                    >
                      {activeBanners[currentSlide]?.title || 'Premium Building Materials'}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 4,
                        opacity: 0.9,
                        fontWeight: 300,
                        fontSize: { xs: '1.2rem', md: '1.5rem' },
                        color: 'white'
                      }}
                    >
                      {activeBanners[currentSlide]?.description || 'Discover the finest marble, granite, and tiles from verified suppliers across India'}
                    </Typography>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                    >
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="large"
                          endIcon={<ArrowForward />}
                          onClick={(e) => handleShopNow(activeBanners[currentSlide], e)}
                          sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                            },
                          }}
                        >
                          Shop Now
                        </Button>
                      </Box>
                    </motion.div>
                  </motion.div>
                </AnimatePresence>
              </Container>
            </Box>
          </Box>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons */}
      {activeBanners.length > 1 && (
        <>
          <NavigationButton
            className="carousel-nav"
            sx={{ left: 16 }}
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <ArrowBackIos />
          </NavigationButton>
          <NavigationButton
            className="carousel-nav"
            sx={{ right: 16 }}
            onClick={nextSlide}
            aria-label="Next slide"
          >
            <ArrowForwardIos />
          </NavigationButton>
        </>
      )}

      {/* Indicators */}
      {activeBanners.length > 1 && (
        <IndicatorContainer>
          {activeBanners.map((_, index) => (
            <Indicator
              key={index}
              isActive={index === currentSlide}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </IndicatorContainer>
      )}
    </CarouselContainer>
  );
};

export default BannerCarousel;